import { Entity, Firm, FirmType } from 'api/interfaces/firm';
import { Form, FormChangeResult } from 'components/Form';
import { isBusinessUnitsEnabled } from 'config/features';
import { onIpAddressesAdded } from 'helpers/ipAddressesListParser';
import { SMSession } from 'interfaces/session';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFirmsById } from 'redux/actions/firms/getFirmsById';
import { getFirmsTypes } from 'redux/actions/firms/getFirmsTypes';
import { setIsConnectivity } from 'redux/actions/firms/setIsConnectivity';
import { ApplicationState } from 'redux/applicationState';
import { sessionsSelector } from 'redux/reducers/sessionsReducer';
import { fetchSessionsAction } from 'redux/services/sessionsServices';
import { useFields } from 'routes/EntityManagement/FirmsManagement/components/FirmsForm/useFields';
import { Brokerage, isValidBrokerageItem } from 'types/brokerage';

interface Props {
  readonly firm: Firm;

  onSave(firm: Firm): void;

  onModifiedChanged?(modified: boolean): void;
  onErrorsChanged?(hasErrors: boolean): void;
}

const FirmsForm: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onSave, onErrorsChanged, onModifiedChanged } = props;
  const { firm } = props;

  const dispatch = useDispatch();
  const sessions = useSelector(sessionsSelector);
  const { firmsById, loadingById, selectedBusinessUnit } = useSelector(
    (state: ApplicationState) => state.firms
  );

  const onEntitiesChange = React.useCallback((value: any, _: Firm): FormChangeResult => {
    const entities: readonly Entity[] = value;

    if (entities.length === 1) {
      return {
        value: entities.map((entity: Entity): Entity => ({ ...entity, default: 1 })),
        error: null,
      };
    }

    const error =
      entities.length === 0
        ? 'No entities defined'
        : entities.every((entity: Entity): boolean => !entity.default)
        ? 'No default entity selected'
        : null;

    return {
      value: entities,
      error: error,
    };
  }, []);

  const onBrokerageChange = React.useCallback((value: Brokerage): FormChangeResult => {
    const keys = Object.keys(value);
    return keys.reduce(
      (result: FormChangeResult, key: string): FormChangeResult => {
        if (result.error !== undefined) {
          return result;
        }

        const error = isValidBrokerageItem(value[key as keyof Brokerage]);
        if (error !== null) {
          return {
            error: `${error} ${key.toUpperCase()}`,
            value: value,
          };
        }

        return result;
      },
      {
        error: undefined,
        value: value,
      }
    );
  }, []);

  const onFormValueChange = React.useCallback(
    (name: keyof Firm, value: any, firm: Firm): FormChangeResult => {
      switch (name) {
        case 'entities':
          return onEntitiesChange(value, firm);
        case 'brokerage':
          return onBrokerageChange(value);
        case 'whitelist':
          return onIpAddressesAdded(value);
        default:
          if (name === 'firmType') {
            dispatch(setIsConnectivity(value === FirmType.Connectivity));
          }
          return { value: value, error: null };
      }
    },
    [onBrokerageChange, onEntitiesChange, dispatch]
  );

  const fields = useFields();
  const firmsProvider = useMemo((): readonly SMSession[] => sessions, [sessions]);

  const filteredFirm = useMemo((): Firm => {
    const currentFirm = firmsById?.[0] || Firm.empty();
    if (!isBusinessUnitsEnabled || !selectedBusinessUnit || !currentFirm.BU?.length) {
      return currentFirm;
    }

    const selectedBUValue =
      typeof selectedBusinessUnit === 'string' ? selectedBusinessUnit : selectedBusinessUnit.BU;

    const filteredSessionIds = currentFirm.sessionIds.filter((session) => {
      return session.name === selectedBUValue;
    });

    return {
      ...currentFirm,
      sessionIds: filteredSessionIds,
    };
  }, [firmsById, selectedBusinessUnit]);

  useEffect((): void => {
    dispatch(fetchSessionsAction());
  }, [dispatch]);

  useEffect((): void => {
    dispatch(getFirmsById(firm.id));
    dispatch(getFirmsTypes());
  }, [dispatch, firm.id]);

  return (
    <Form
      fields={fields}
      value={filteredFirm}
      provider={firmsProvider}
      onChange={onFormValueChange}
      onSubmit={onSave}
      onErrorsChanged={onErrorsChanged}
      onModifiedChanged={onModifiedChanged}
      loading={loadingById}
    />
  );
};

export default FirmsForm;
